<template>
  <div class="main-scene" @click="handleSceneClick">
    <!-- Background GIF -->
    <img :src="backgroundGif" alt="World" class="background" />

    <!-- Clickable Suns -->
    <img v-for="sun in suns" :key="sun.time" :src="sun.image" :style="sun.style" @click.stop="handleSunClick(sun)"
      alt="sun-phase" class="clickable-sun glow-image" />

    <!-- Sandwich Button -->
    <button class="sandwich-button" @click="toggleMenu">
      ☰
    </button>

    <!-- Backdrop and Menu -->
    <div v-if="isMenuVisible" class="backdrop2" @click="closeMenu"></div>
    <div v-if="isMenuVisible" class="menu">
      <button class="menu-option" @click="navigateToProfile">{{ profile_label[language] }}</button>
      <button class="menu-option" @click="startBuildYourWeek">{{ buildWeek_label[language] }}</button>
      <button class="menu-option" @click="navigateToScoreboard">{{ scoreboard_label[language] }}</button>
      <button class="menu-option" @click="playIntroVideo">{{ intro_label[language] }}</button>
      <button class="menu-option" @click="navigateToAbout">{{ about_label[language] }}</button>
      <button class="menu-option" @click="logout">{{ logout_label[language] }}</button>
    </div>
    <div v-if="lastSunClicked" class="sun-label">
      {{ sunLabels[language][lastSunClicked] }}
    </div>

    <CinematicOverlay v-if="isOverlayVisible" :message="overlayMessage[language]" :visible="isOverlayVisible"
      @start-game="onStartGame" @video-ended="onVideoEnded" />
    <transition :name="instantBackdropDismiss ? 'none' : 'fade-backdrop'">
      <div v-if="focusSeeds" class="focus-backdrop"></div>
    </transition>
    <div v-if="todaysSeeds !== null" class="seeds-container">
      <div class="seeds-title-wrapper">
        <transition name="fade">
          <div v-if="showInfo" class="seeds-title">{{ todays_seeds_label[language] }}</div>
        </transition>
      </div>
      <div class="seeds-row">
        <div class="seed-box blue">{{ todaysSeeds.blue || 0 }}</div>
        <div class="seed-box red">{{ todaysSeeds.red || 0 }}</div>
        <div class="seed-box green">{{ todaysSeeds.green || 0 }}</div>
        <div class="seed-box yellow">{{ todaysSeeds.yellow || 0 }}</div>
      </div>
    </div>
    <div class="motivation-container">
      <transition name="fade">
        <div v-if="showMotivation" class="motivation-box">
          <h2 class="motivation-text">{{ currentMotivation }}</h2>
        </div>
      </transition>
    </div>
    <div class="professor-pep-container">
      <img
        :src="profPepImage"
        alt="Professor Pep"
        :class="['professor-pep', isPepStill ? 'prof-glow-breathe' : '']"
        @click="showRandomMotivation"
      />
    </div>
  </div>



</template>

<script>
import { sounds } from '@/utils/sounds';
import CinematicOverlay from '@/components/CinematicOverlay.vue';

export default {
  components: {
    CinematicOverlay,
  },
  data() {
    const widthMultiplier = window.innerWidth / 1024;
    const heightMultiplier = window.innerHeight / 1792;

    return {
      profPepImage: require('@/assets/game-characters/professor_pep_talking.gif'),
      profPepTalking: require('@/assets/game-characters/professor_pep_talking.gif'),
      profPepStill: require('@/assets/game-characters/prof-pep.png'),
      audio: sounds.mainTheme,
      clickSounds: sounds.click_sun,
      buttonClickSound: sounds.buttonClick,
      profile_label: { en: "Profile", el: "Προφίλ", it: "Profilo", ro: "Profil", uk: "Профіль" },
      about_label: { en: "About", el: "Περί", it: "Informazioni", ro: "Despre", uk: "Про нас" },
      buildWeek_label: { en: "Build Your Week", el: "Οργάνωση διατροφής", it: "Crea la tua settimana", ro: "Construiește-ți săptămâna", uk: "Створи свій тиждень" },
      scoreboard_label: { en: "Scoreboard", el: "Πίνακας βαθμολογίας", it: "Classifica", ro: "Tabel de scoruri", uk: "Таблиця результатів" },
      logout_label: { en: "Logout", el: "Αποσύνδεση", it: "Disconnetti", ro: "Deconectare", uk: "Вийти" },
      intro_label: { en: "Intro video", el: "Εισαγωγικό βίντεο", it: "Video introduttivo", ro: "Video introductiv", uk: "Вступне відео" },
      todays_seeds_label: {
        en: "Seeds earned today",
        el: "Σπόροι που κέρδισες σήμερα",
        it: "Semi guadagnati oggi",
        ro: "Semințe câștigate astăzi",
        uk: "Насіння, зібране сьогодні"
      },
      motivation_messages: {
        en: [
          "Together we keep Vitalia blooming!",
          "Your actions nourish the Eternal Olive Tree!",
          "Every seed brings life back to Vitalia!",
          "Guardians like you keep the balance alive!",
          "The Rainbow Seeds need your care to shine.",
          "Help the Nature Spirits restore harmony today!",
          "The Eternal Olive Tree grows stronger with every seed you find.",
          "Stand against the Greedlings and protect Vitalia.",
          "Vitalia’s shining lakes thank you for your courage.",
          "The forests whisper their gratitude for every seed you return.",
          "Vitalia shines brighter with your help.",
          "The Spirits reveal themselves to true Guardians like you.",
          "Your quest keeps the elements (water, air, soil, and fire) in harmony.",
          "Vitalia’s meadows bloom when the Guardians complete their tasks.",
          "Your courage restores the seeds the Greedlings stole.",
          "The Eternal Olive Tree draws strength from your dedication.",
          "Balance returns to Vitalia through your daily actions.",
          "Guardians unite to keep pollution and waste away.",
          "The Rainbow Seeds sparkle because of you."
        ],
        el: [
          "Μαζί κρατάμε τη Vitalia ανθισμένη!",
          "Οι πράξεις σου θρέφουν την Αιώνια Ελιά!",
          "Κάθε σπόρος φέρνει τη ζωή πίσω στη Vitalia!",
          "Οι Φύλακες σαν εσένα κρατούν την ισορροπία ζωντανή!",
          "Οι Σπόροι του Ουράνιου Τόξου χρειάζονται τη φροντίδα σου για να λάμψουν.",
          "Βοήθησε τα Πνεύματα της Φύσης να επαναφέρουν την αρμονία σήμερα!",
          "Η Αιώνια Ελιά δυναμώνει με κάθε σπόρο που βρίσκεις.",
          "Στάσου ενάντια στους Greedlings και προστάτεψε τη Vitalia.",
          "Οι λαμπερές λίμνες της Vitalia σε ευχαριστούν για το θάρρος σου.",
          "Τα δάση ψιθυρίζουν την ευγνωμοσύνη τους για κάθε σπόρο που επιστρέφεις.",
          "Η Vitalia λάμπει περισσότερο με τη βοήθειά σου.",
          "Τα Πνεύματα αποκαλύπτονται σε αληθινούς Φύλακες σαν εσένα.",
          "Η αποστολή σου κρατά τα στοιχεία (νερό, αέρα, έδαφος και φωτιά) σε αρμονία.",
          "Τα λιβάδια της Vitalia ανθίζουν όταν οι Φύλακες ολοκληρώνουν τις αποστολές τους.",
          "Το θάρρος σου επαναφέρει τους σπόρους που έκλεψαν οι Greedlings.",
          "Η Αιώνια Ελιά αντλεί δύναμη από την αφοσίωσή σου.",
          "Η ισορροπία επιστρέφει στη Vitalia μέσα από τις καθημερινές σου πράξεις.",
          "Οι Φύλακες ενώνουν τις δυνάμεις τους για να κρατήσουν τη ρύπανση και τα απόβλητα μακριά.",
          "Οι Σπόροι του Ουράνιου Τόξου λάμπουν χάρη σε εσένα."
        ],
        it: [
          "Insieme manteniamo Vitalia in fiore!",
          "Le tue azioni nutrono l’Ulivo Eterno!",
          "Ogni seme riporta la vita a Vitalia!",
          "I Guardiani come te mantengono vivo l’equilibrio!",
          "I Semi Arcobaleno hanno bisogno delle tue cure per brillare.",
          "Aiuta gli Spiriti della Natura a ristabilire l’armonia oggi!",
          "L’Ulivo Eterno diventa più forte con ogni seme che trovi.",
          "Affronta i Greedlings e proteggi Vitalia.",
          "I laghi splendenti di Vitalia ti ringraziano per il tuo coraggio.",
          "Le foreste sussurrano la loro gratitudine per ogni seme che restituisci.",
          "Vitalia splende di più grazie al tuo aiuto.",
          "Gli Spiriti si rivelano ai veri Guardiani come te.",
          "La tua missione mantiene in armonia gli elementi (acqua, aria, terra e fuoco).",
          "I prati di Vitalia fioriscono quando i Guardiani portano a termine i loro compiti.",
          "Il tuo coraggio restituisce i semi rubati dai Greedlings.",
          "L’Ulivo Eterno trae forza dalla tua dedizione.",
          "L’equilibrio torna a Vitalia grazie alle tue azioni quotidiane.",
          "I Guardiani si uniscono per tenere lontani inquinamento e sprechi.",
          "I Semi Arcobaleno brillano grazie a te."
        ],
        ro: [
          "Împreună păstrăm Vitalia înfloritoare!",
          "Acțiunile tale hrănesc Măslinul Etern!",
          "Fiecare sămânță aduce viața înapoi în Vitalia!",
          "Gardieni ca tine mențin echilibrul viu!",
          "Semințele Curcubeu au nevoie de grija ta ca să strălucească.",
          "Ajută Spiritele Naturii să refacă armonia astăzi!",
          "Măslinul Etern devine mai puternic cu fiecare sămânță pe care o găsești.",
          "Înfruntă-i pe Greedlings și protejează Vitalia.",
          "Lacurile strălucitoare ale Vitaliei îți mulțumesc pentru curaj.",
          "Pădurile își șoptesc recunoștința pentru fiecare sămânță pe care o returnezi.",
          "Vitalia strălucește mai tare cu ajutorul tău.",
          "Spiritele se arată adevăraților Gardieni ca tine.",
          "Misiunea ta păstrează în armonie elementele (apă, aer, sol și foc).",
          "Pajiștile Vitaliei înfloresc când Gardienii își îndeplinesc sarcinile.",
          "Curajul tău readuce semințele furate de Greedlings.",
          "Măslinul Etern își trage puterea din devotamentul tău.",
          "Echilibrul revine în Vitalia prin acțiunile tale zilnice.",
          "Gardienii se unesc pentru a ține departe poluarea și risipa.",
          "Semințele Curcubeu strălucesc datorită ție."
        ],
        uk: [
          "Разом ми зберігаємо Віталію квітучою!",
          "Твої дії живлять Вічне Оливкове Дерево!",
          "Кожна насінина повертає життя у Віталію!",
          "Охоронці, як ти, підтримують рівновагу!",
          "Веселкове насіння потребує твоєї турботи, щоб сяяти.",
          "Допоможи Духам Природи відновити гармонію сьогодні!",
          "Вічне Оливкове Дерево міцнішає з кожною насіниною, яку ти знаходиш.",
          "Протистій Greedlings і захищай Віталію.",
          "Блискучі озера Віталії дякують тобі за сміливість.",
          "Ліси шепочуть подяку за кожну повернуту насінину.",
          "Віталія сяє яскравіше завдяки тобі.",
          "Духи відкриваються справжнім Охоронцям, таким як ти.",
          "Твоє завдання тримає в гармонії стихії (воду, повітря, ґрунт і вогонь).",
          "Луки Віталії розквітають, коли Охоронці виконують свої завдання.",
          "Твоя відвага повертає насіння, яке вкрали Greedlings.",
          "Вічне Оливкове Дерево черпає силу з твоєї відданості.",
          "Рівновага повертається у Віталію завдяки твоїм щоденним вчинкам.",
          "Охоронці об’єднуються, щоб тримати подалі забруднення і відходи.",
          "Веселкове насіння сяє завдяки тобі."
        ]
      },
      currentMotivation: "",

      isOverlayVisible: false,
      overlayMessage: { en: "Welcome to the game!", el: "Καλώς ήρθατε στο παιχνίδι!", it: "Benvenuto al gioco!", ro: "Bun venit în joc!", uk: "Ласкаво просимо до гри!" },

      backgroundGif: '',
      backgroundGifMin: require('@/assets/world/minimum.gif'), // Path to your GIF asset
      backgroundGifMed: require('@/assets/world/medium.gif'), // Path to your GIF asset
      backgroundGifMax: require('@/assets/world/maximum.gif'), // Path to your GIF asset

      isMenuVisible: false,
      sunLabels: {
        en: { morning: 'Morning', noon: 'Noon', afternoon: 'Afternoon', night: 'Evening' },
        el: { morning: 'Πρωί', noon: 'Μεσημέρι', afternoon: 'Απόγευμα', night: 'Βράδυ' },
        it: { morning: 'Mattina', noon: 'Mezzogiorno', afternoon: 'Pomeriggio', night: 'Sera' },
        ro: { morning: 'Dimineață', noon: 'Amiază', afternoon: 'După-amiază', night: 'Seară' },
        uk: { morning: 'Ранок', noon: 'Полудень', afternoon: 'Після обіду', night: 'Вечір' }
      },
      suns: [
        {
          time: 'morning',
          image: require('@/assets/suns/morning-sun.png'),
          style: { top: `${290 * heightMultiplier}px`, left: `${149 * widthMultiplier}px`, width: `${218 * 4 / 5 * widthMultiplier}px`, height: `${215 * 4 / 5 * widthMultiplier}px` }, // Smaller size
        },
        {
          time: 'noon',
          image: require('@/assets/suns/noon-sun.png'),
          style: { top: `${190 * heightMultiplier}px`, left: `${340 * widthMultiplier}px`, width: `${218 * 4 / 5 * widthMultiplier}px`, height: `${215 * 4 / 5 * widthMultiplier}px` },
        },
        {
          time: 'afternoon',
          image: require('@/assets/suns/afternoon-sun.png'),
          style: { top: `${210 * heightMultiplier}px`, left: `${560 * widthMultiplier}px`, width: `${218 * 4 / 5 * widthMultiplier}px`, height: `${215 * 4 / 5 * widthMultiplier}px` },
        },
        {
          time: 'night',
          image: require('@/assets/suns/sunset-sun.png'),
          style: { top: `${310 * heightMultiplier}px`, left: `${750 * widthMultiplier}px`, width: `${218 * 4 / 5 * widthMultiplier}px`, height: `${215 * 4 / 5 * widthMultiplier}px` },
        },
      ],
      lastSunClicked: null,
      todaysSeeds: null,
      showInfo: false,
      motivation_label: {
        en: "Together we keep Vitalia blooming!",
        el: "Μαζί κρατάμε τη Vitalia ανθισμένη!",
        it: "Insieme manteniamo Vitalia in fiore!",
        ro: "Împreună menținem Vitalia înfloritoare!",
        uk: "Разом ми зберігаємо Віталію квітучою!"
      },
      showMotivation: false,
      focusSeeds: false,
      focusSeedsTimeout: null,
      instantBackdropDismiss: false,
    };
  },
  computed: {
    isPepStill() {
      // Apply glow only when the still image is active
      return this.profPepImage === this.profPepStill;
    },
  },
  methods: {
    showRandomMotivation() {
      const msgs = this.motivation_messages[this.language];
      this.profPepImage = this.profPepTalking;
      this.currentMotivation = msgs[Math.floor(Math.random() * msgs.length)];
      this.showMotivation = true;
      setTimeout(() => {
        this.showMotivation = false;
        this.profPepImage = this.profPepStill;
      }, 10000);
    },

    handleSunClick(sun) {
      if (this.focusSeeds) this.dismissFocusSeeds();
      if (this.lastSunClicked === sun.time) {
        const sound = this.clickSounds[sun.time];
        if (sound) {
          sound.play();
        }
        this.navigateToGameSelection(sun);
        this.lastSunClicked = null;
        return;
      }
      this.lastSunClicked = sun.time;
    },
    handleSceneClick() {
      this.lastSunClicked = null;
      this.dismissFocusSeeds();
    },
    dismissFocusSeeds() {
      // Clear any pending auto-hide
      if (this.focusSeedsTimeout) {
        clearTimeout(this.focusSeedsTimeout);
        this.focusSeedsTimeout = null;
      }
      // Hide immediately without fade
      if (this.focusSeeds) {
        this.instantBackdropDismiss = true; // switch transition to 'none'
        this.focusSeeds = false;
        this.$nextTick(() => {
          // restore fade transitions for the next time it's shown
          this.instantBackdropDismiss = false;
        });
      }
    },
    toggleMenu() {
      this.buttonClickSound.play();
      this.isMenuVisible = !this.isMenuVisible;
    },
    closeMenu() {
      this.buttonClickSound.play();
      this.isMenuVisible = false;
    },
    startBuildYourWeek() {
      this.buttonClickSound.play();
      this.closeMenu();
      this.$router.push({ name: 'BuildWeekGame' });
    },
    navigateToScoreboard() {
      this.buttonClickSound.play();
      this.closeMenu();
      this.$router.push({ name: 'PersonalScoreboard' });
    },
    navigateToProfile() {
      this.buttonClickSound.play();
      this.closeMenu();
      this.$router.push({ name: 'UserProfile' });
    },
    navigateToAbout() {
      this.buttonClickSound.play();
      this.closeMenu();
      this.$router.push({ name: 'AboutVitalia' });
    },
    playIntroVideo() {
      this.buttonClickSound.play();
      if (this.audio) {
        this.audio.stop();
        this.audio = null;
      }
      this.closeMenu();
      this.isOverlayVisible = true;
    },
    logout() {
      this.buttonClickSound.play();
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      this.$router.push({ name: "UserAuth" });
    },

    navigateToGameSelection(sun) {
      // Get the current day of the week (0 = Sunday, 6 = Saturday)
      const currentDay = new Date().getDay();

      const afternoonGameData = {
        default: {
          background: require('@/assets/game-backgrounds/background.jpg'),
          // games: ['Body Move', 'Jump Rope', 'Jumps Precision', 'Jumps', 'Running', 'Frames', 'Hands'],
          games: ['Body Move', 'Jump Rope', 'High Knees', 'Jumps', 'Running', 'Frames', 'Hands Game', 'Food Matching', 'Food Pyramid', 'Select Meal', 'Find the Odd'],
        },
        specialDays: {
          background: require('@/assets/game-backgrounds/background.jpg'),
          // games: ['Situps', 'Pushups', 'Plank', 'Jumping Jacks', 'High Knees'],
          games: ['Situps', 'Pushups', 'Plank', 'Jumping Jacks', 'Jumps Precision', 'Food Matching', 'Food Pyramid', 'Select Meal', 'Find the Odd'],
        },
      };

      //['Select Meal','Find the Odd','Build Your Week','Nutrient Matching','Food Pyramid']

      //['Situps','Running','Pushups','Jumping Jacks','Jumps','High Knees','Hands','Frames','Body Move','Jump Rope']

      const gameData = {
        morning: {
          background: require('@/assets/game-backgrounds/background.jpg'),
          games: ['Stretching', 'Select Meal', 'Find the Odd'],
        },
        noon: {
          background: require('@/assets/game-backgrounds/background.jpg'),
          games: ['Food Matching', 'Food Pyramid', 'Select Meal', 'Find the Odd'],
        },
        afternoon:
          // sun.time === 'afternoon' && [1, 3, 6].includes(currentDay) // Monday, Wednesday, Saturday
          sun.time === 'afternoon' && [].includes(currentDay)
            ? afternoonGameData.specialDays
            : afternoonGameData.default,
        night: {
          background: require('@/assets/game-backgrounds/background.jpg'),
          games: ['Stretching', 'Select Meal', 'Find the Odd'],
        },
      };

      this.$router.push({
        name: 'GameSelection',
        params: {
          time: sun.time,
          background: gameData[sun.time].background,
          games: JSON.stringify(gameData[sun.time].games),
        },
      });
    },
    async checkUserProfile() {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await fetch("https://erebus2.athenarc.gr/duse/check-profile", {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await response.json();
        if (!data.hasProfile) {
          this.$router.push({ name: "UserProfile" }); // Redirect if no profile
        }
      } catch (error) {
        console.error("Error checking profile:", error);
      }
    },


    async changeBackground() {
      const token = localStorage.getItem("token");
      if (!token) {
        this.backgroundGif = this.backgroundGifMed;
        this.todaysSeeds = null;
        return;
      }

      try {
        const response = await fetch("https://erebus2.athenarc.gr/duse/get-todays-seed-points", {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await response.json();
        const seeds = data.seeds;

        if (!seeds) {
          this.backgroundGif = this.backgroundGifMed;
          this.todaysSeeds = null;
          return;
        }

        const values = Object.values(seeds);
        const hasZero = values.some(count => count === 0);
        const isExactlyOneEach = values.every(count => count === 1);
        const hasMoreThanOne = values.some(count => count > 1);

        if (hasZero) {
          this.backgroundGif = this.backgroundGifMin;
        } else if (isExactlyOneEach) {
          this.backgroundGif = this.backgroundGifMed;
        } else if (hasMoreThanOne) {
          this.backgroundGif = this.backgroundGifMax;
        } else {
          this.backgroundGif = this.backgroundGifMed;
        }
        this.todaysSeeds = seeds;
        // Briefly focus attention on the seeds when they are present
        if (this.todaysSeeds) {
          // clear any previous timer and ensure we start a fresh one
          if (this.focusSeedsTimeout) {
            clearTimeout(this.focusSeedsTimeout);
            this.focusSeedsTimeout = null;
          }
          this.focusSeeds = true;
          this.focusSeedsTimeout = setTimeout(() => {
            this.focusSeeds = false;
            this.focusSeedsTimeout = null;
          }, 2000);
        }

      } catch (error) {
        console.error("Error getting today's seed points:", error);
        this.backgroundGif = this.backgroundGifMed;
        this.todaysSeeds = null;
      }
    },
    onStartGame() {
      this.isOverlayVisible = false;
      // Optionally navigate to main game scene or keep user on MainScene
    },
    onVideoEnded() {
      this.isOverlayVisible = false;
      // Optionally handle post-video actions
    },
  },
  created() {
    this.language = localStorage.getItem("language") || "en"; // Default to English
    console.log("LANGUAGE " + this.language);
  },
  mounted() {
    const msgs = this.motivation_messages[this.language];
    this.currentMotivation = msgs[Math.floor(Math.random() * msgs.length)];
    this.audio.play();
    this.checkUserProfile(); // Run on page load
    this.changeBackground();
    this.showInfo = true;
    setTimeout(() => { this.showInfo = false; }, 5000);
    this.showMotivation = true;
    // Set Professor Pep to talking when first motivational message shows
    this.profPepImage = this.profPepTalking;
    setTimeout(() => {
      this.showMotivation = false;
      // Set Professor Pep to still when initial message disappears
      this.profPepImage = this.profPepStill;
    }, 10000);
  },

  beforeUnmount() {
    if (this.audio) {
      this.audio.stop();
      this.audio = null;
    }
  },

};
</script>


<style>
/* Lock the scene in place */
.main-scene {
  width: 100vw;
  height: 100vh;
  position: fixed;
  /* Fixes the scene to the viewport */
  top: 0;
  left: 0;
  overflow: hidden;
  /* Prevent any accidental scrolling */
  z-index: 0;
  /* Ensure it's below other UI elements */
}

/* Fixed background GIF */
.background {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Ensures proper scaling */
  position: absolute;
  /* Ensures the background fills the container */
  top: 0;
  left: 0;
  z-index: 1;
}

/* Clickable suns positioned relative to the fixed scene */
.clickable-sun {
  position: absolute;
  /* Position relative to the .main-scene */
  z-index: 2;
  /* Ensure they appear above the background */
  cursor: pointer;
  /* Interactive pointer */
  transition: transform 0.3s ease;
  /* Smooth scaling on hover */
  border-radius: 50%;
  /* Ensures perfect circle for suns */
}

/* Add hover effect to suns */
.clickable-sun:hover {
  transform: scale(1.2);
  /* Slight zoom effect */
}

.glow-image {
  width: 150px;
  /* Adjust to your desired size */
  height: 150px;
  /* Same as width to maintain round shape */
  border-radius: 50%;
  /* Makes the image circular */
  box-shadow: 0 0 15px #ffecb3, 0 0 25px #ffe0b2, 0 0 35px #ffffff, 0 0 45px #ffffff;
  filter: hue-rotate(0deg);
  animation: glow-breathe 3s ease-in-out infinite;
}

@keyframes glow-breathe {

  0%,
  100% {
    box-shadow: 0 0 15px #ffecb3, 0 0 20px #ffe0b2, 0 0 25px #ffffff, 0 0 30px #ffffff;
    filter: hue-rotate(0deg);
  }

  50% {
    box-shadow: 0 0 20px #ffecb3, 0 0 25px #ffe0b2, 0 0 35px #ffffff, 0 0 40px #ffffff;
    filter: hue-rotate(10deg);
  }
}

/* Prevent scrolling of the page */
body {
  margin: 0;
  overflow: hidden;
  /* Prevent scrolling on the body */
}


.sandwich-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  font-size: 24px;
  border-radius: 5px;
  padding: 10px;
  cursor: pointer;
  z-index: 1001;
}

.backdrop2 {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.menu {
  position: absolute;
  top: 50px;
  right: 20px;
  background: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  z-index: 1002;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.menu-option {
  background-color: #0077cc;
  color: white;
  border: none;
  padding: 10px;
  text-align: center;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
}

.menu-option:hover {
  background-color: #005fa3;
}

.sun-label {
  position: fixed;
  top: 5%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 18px;
  z-index: 10;
  text-align: center;
  pointer-events: none;
}

.main-scene .seeds-container {
  position: fixed;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  z-index: 1003;
}

.main-scene .seed-box {
  padding: 8px 12px;
  border-radius: 5px;
  color: white;
  font-size: 32px;
  font-weight: bold;
  min-width: 50px;
  text-align: center;
  -webkit-text-stroke: 2px black;
}

.main-scene .blue {
  background-image: url('@/assets/seeds/blue_seed.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.main-scene .red {
  background-image: url('@/assets/seeds/red_seed.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.main-scene .green {
  background-image: url('@/assets/seeds/green_seed.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.main-scene .yellow {
  background-image: url('@/assets/seeds/yellow_seed.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.seeds-title {
  width: 100%;
  text-align: center;
  color: white;
  font-size: 2.5vh;
  font-weight: bold;
  -webkit-text-stroke: 1px black;
  margin-bottom: 10px;
  white-space: nowrap;
}

.seeds-row {
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: center;
}

.seeds-title-wrapper {
  height: 3vh; /* adjust to match the .seeds-title font-size and margin */
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}


.motivation-container {
  position: fixed;
  bottom: 10%;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  gap: 10px;
  z-index: 1001;
}

.motivation-box {
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 8px 12px;
  max-width: 60vw;
  white-space: normal;
  word-wrap: break-word;
  text-align: center;
}

.professor-pep {
  height: 20vh !important;
  width: auto !important;
  max-height: none !important;
  max-width: none !important;
  display: block;
}

/* Breathing glow for Professor Pep when still */
.prof-glow-breathe {
  animation: pep-glow 3s ease-in-out infinite;
  will-change: filter;
}

@keyframes pep-glow {
  0%, 100% {
    filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.6))
            drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));
  }
  50% {
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.9))
            drop-shadow(0 0 18px rgba(255, 255, 255, 0.6));
  }
}

.motivation-text {
  font-size: 2vh;
  font-weight: bold;
  text-align: center;
  -webkit-text-stroke: 0.5px black;
  z-index: 1003;
}

.professor-pep-container {
  position: fixed;
  bottom: 0%;
  left: 85%;
  transform: translateX(-50%);
  z-index: 1001;
}

.focus-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(3px) saturate(80%);
  pointer-events: none; /* don’t block clicks */
  will-change: opacity;
  z-index: 1002; /* below seeds (1003), above world */
}

.fade-backdrop-enter-active,
.fade-backdrop-leave-active {
  transition: opacity 500ms ease;
}
.fade-backdrop-enter-from,
.fade-backdrop-leave-to {
  opacity: 0;
}
</style>

